import { useMemo } from "react";
import { FeatureFlagKey } from "./flags";
import { UseFlagReadyOptions, useFlagReady } from "./useFlagReady";

export type BooleanFlagStateValue = "pending" | "enabled" | "disabled";

export type BooleanFlagState<K extends FeatureFlagKey = FeatureFlagKey> = {
  key: K;
  state: BooleanFlagStateValue;
  ready: boolean;
  value: boolean;
  isEnabled: boolean;
  isDisabled: boolean;
};

const resolveBooleanFlagState = (ready: boolean, resolvedValue: boolean): BooleanFlagStateValue => {
  if (!ready) {
    return "pending";
  }

  return resolvedValue ? "enabled" : "disabled";
};

export const useBooleanFlagState = <K extends FeatureFlagKey>(
  key: K,
  options: UseFlagReadyOptions = {},
): BooleanFlagState<K> => {
  const { ready, value } = useFlagReady(key, options);
  const resolvedValue = Boolean(value);

  return useMemo(
    () => ({
      key,
      state: resolveBooleanFlagState(ready, resolvedValue),
      ready,
      value: ready ? resolvedValue : false,
      isEnabled: ready ? resolvedValue : false,
      isDisabled: ready ? !resolvedValue : false,
    }),
    [key, ready, resolvedValue],
  );
};
